<template>
  <b-modal :active.sync="isModalActive" :width="760" scroll="keep" @close="onModalClose">
    <div class="weekly-linked-boq-roots-modal">
      <div class="weekly-linked-boq-roots-modal__header">
        <div class="weekly-linked-boq-roots-modal__title-row">
          <b-icon pack="fas" icon="ruler-combined" size="is-small" class="weekly-linked-boq-roots-modal__title-icon" />
          <h2 class="weekly-linked-boq-roots-modal__title">
            {{ $t('weekly_linked_boq_roots_title') }}
          </h2>
        </div>
        <p v-if="activityName" class="weekly-linked-boq-roots-modal__subtitle">
          {{ activityName }}
        </p>
        <p
          v-if="showPrimaryWarning"
          class="weekly-linked-boq-roots-modal__primary-warning"
        >
          <b-icon pack="fas" icon="exclamation-triangle" size="is-small" class="weekly-linked-boq-roots-modal__primary-warning-icon" />
          {{ $t('weekly_linked_boq_no_primary_hint') }}
        </p>
      </div>

      <div class="weekly-linked-boq-roots-modal__table-wrap overflow-x-auto">
        <table class="weekly-linked-boq-roots-modal__table w-full text-xs border-collapse min-w-max">
          <thead>
            <tr>
              <th class="weekly-linked-boq-roots-modal__th weekly-linked-boq-roots-modal__th--star" />
              <th class="weekly-linked-boq-roots-modal__th weekly-linked-boq-roots-modal__th--left">{{ $t('code') }}</th>
              <th class="weekly-linked-boq-roots-modal__th weekly-linked-boq-roots-modal__th--left">{{ $t('chapter') }}</th>
              <th class="weekly-linked-boq-roots-modal__th weekly-linked-boq-roots-modal__th--left">{{ $t('name') }}</th>
              <th class="weekly-linked-boq-roots-modal__th">{{ $t('measurement_unit_short') }}</th>
              <th class="weekly-linked-boq-roots-modal__th weekly-linked-boq-roots-modal__th--right">{{ $t('quantity') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in rows"
              :key="item.id"
              class="weekly-linked-boq-roots-modal__row"
            >
              <td class="weekly-linked-boq-roots-modal__td text-center">
                <button
                  v-if="canSetPrimary"
                  type="button"
                  class="weekly-linked-boq-roots-modal__star-btn"
                  :class="{
                    'weekly-linked-boq-roots-modal__star-btn--on': item.is_primary,
                    'weekly-linked-boq-roots-modal__star-btn--off': !item.is_primary,
                    'weekly-linked-boq-roots-modal__star-btn--loading': isSettingPrimary(item.id),
                  }"
                  :disabled="isSettingPrimary(item.id) || item.is_primary"
                  :aria-label="$t('weekly_linked_boq_set_primary')"
                  :title="$t('weekly_linked_boq_set_primary')"
                  @click="setPrimary(item)"
                >
                  <b-icon
                    pack="fas"
                    :icon="isSettingPrimary(item.id) ? 'spinner' : 'star'"
                    size="is-small"
                    :custom-class="isSettingPrimary(item.id) ? 'fa-spin' : null"
                  />
                </button>
                <b-icon
                  v-else
                  pack="fas"
                  icon="star"
                  size="is-small"
                  :class="item.is_primary
                    ? 'weekly-linked-boq-roots-modal__star--on'
                    : 'weekly-linked-boq-roots-modal__star--off'"
                />
              </td>
              <td class="weekly-linked-boq-roots-modal__td weekly-linked-boq-roots-modal__code whitespace-nowrap">
                {{ item.code || '—' }}
              </td>
              <td class="weekly-linked-boq-roots-modal__td whitespace-nowrap">
                {{ item.chapter_code || '—' }}
              </td>
              <td class="weekly-linked-boq-roots-modal__td weekly-linked-boq-roots-modal__name">
                {{ item.name || '—' }}
              </td>
              <td class="weekly-linked-boq-roots-modal__td text-center whitespace-nowrap">
                {{ item.unit || '—' }}
              </td>
              <td class="weekly-linked-boq-roots-modal__td text-right whitespace-nowrap">
                <span class="weekly-linked-boq-roots-modal__qty">{{ formatQuantity(item.quantity) }}</span>
                <span v-if="item.unit" class="weekly-linked-boq-roots-modal__qty-unit">{{ item.unit }}</span>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="totalLinked">
            <tr class="weekly-linked-boq-roots-modal__total-row">
              <td colspan="5" class="weekly-linked-boq-roots-modal__total-label">
                {{ $t('weekly_linked_boq_total_quantity') }}
              </td>
              <td class="weekly-linked-boq-roots-modal__td text-right whitespace-nowrap">
                <span class="weekly-linked-boq-roots-modal__qty weekly-linked-boq-roots-modal__qty--total">
                  {{ formatQuantity(totalLinked.value) }}
                </span>
                <span class="weekly-linked-boq-roots-modal__qty-unit">{{ totalLinked.unit }}</span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <p class="weekly-linked-boq-roots-modal__hint">
        {{ $t('weekly_linked_boq_roots_readonly_hint') }}
      </p>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios'

function resolveApiIsPrimary(value) {
  return value === true || value === 1 || value === '1'
}

function resolveUnit(boqRoot) {
  if (!boqRoot || !boqRoot.measure_unit) {
    return ''
  }
  return boqRoot.measure_unit.code || boqRoot.measure_unit.name || ''
}

function resolveChapterCode(boqRoot) {
  if (!boqRoot || !boqRoot.parent) {
    return ''
  }
  return boqRoot.parent.code || ''
}

function mapBoqRootRow(boqRoot) {
  return {
    id: boqRoot.id,
    is_primary: resolveApiIsPrimary(boqRoot.is_primary),
    code: boqRoot.code || '',
    chapter_code: resolveChapterCode(boqRoot),
    name: boqRoot.name || '',
    unit: resolveUnit(boqRoot),
    quantity: parseBoqRootQuantity(boqRoot.quantity),
  }
}

function parseBoqRootQuantity(value) {
  if (value === null || value === undefined || value === '') {
    return null
  }
  const normalized = String(value).trim().replace(',', '.')
  const num = parseFloat(normalized)
  return Number.isNaN(num) ? null : num
}

export default {
  name: 'weekly-linked-boq-roots-modal',

  emits: ['primary-changed'],

  data() {
    return {
      isModalActive: false,
      activityId: null,
      projectId: null,
      activityName: null,
      rows: [],
      settingPrimaryId: null,
    }
  },

  computed: {
    canSetPrimary() {
      return this.rows.length >= 2
    },

    hasPrimary() {
      return this.rows.some((row) => row.is_primary)
    },

    showPrimaryWarning() {
      return this.canSetPrimary && !this.hasPrimary
    },

    totalLinked() {
      if (!this.rows.length) {
        return null
      }

      const unit = this.rows[0].unit
      if (!unit) {
        return null
      }

      const allSameUnit = this.rows.every((row) => row.unit === unit)
      if (!allSameUnit) {
        return null
      }

      const sum = this.rows.reduce((acc, row) => {
        const qty = parseFloat(row.quantity)
        return acc + (Number.isNaN(qty) ? 0 : qty)
      }, 0)

      return { value: sum, unit }
    },
  },

  methods: {
    show({
      activityId = null,
      projectId = null,
      activityName = null,
      boqRoots = [],
    } = {}) {
      this.activityId = activityId
      this.projectId = projectId
      this.activityName = activityName
      this.rows = (Array.isArray(boqRoots) ? boqRoots : []).map(mapBoqRootRow)
      this.settingPrimaryId = null
      this.isModalActive = true
    },

    close() {
      this.isModalActive = false
    },

    onModalClose() {
      this.activityId = null
      this.projectId = null
      this.activityName = null
      this.rows = []
      this.settingPrimaryId = null
    },

    isSettingPrimary(boqRootId) {
      return this.settingPrimaryId === boqRootId
    },

    async setPrimary(item) {
      if (!item || item.is_primary || !this.canSetPrimary) {
        return
      }
      if (!this.projectId || !this.activityId) {
        this.$notify.error('error_loading_data')
        return
      }

      this.settingPrimaryId = item.id

      try {
        const { data } = await axios.put(
          `/api/v2/project/${this.projectId}/activity/${this.activityId}/boq-roots/${item.id}/primary`
        )

        if (!data || !data.success) {
          const msg = (data && data.message) ? data.message : 'assignment_change_fail'
          this.$notify.error(this.$t(msg))
          return
        }

        this.rows = this.rows.map((row) => ({
          ...row,
          is_primary: row.id === item.id,
        }))

        this.$emit('primary-changed', {
          activityId: this.activityId,
          boqRootId: item.id,
        })
        this.$notify.success('weekly_linked_boq_primary_saved')
      } catch (error) {
        const msg = (error.response && error.response.data && error.response.data.message)
          || 'assignment_change_fail'
        this.$notify.error(this.$t(msg))
      } finally {
        this.settingPrimaryId = null
      }
    },

    formatQuantity(value) {
      if (value === null || value === undefined || value === '') {
        return '—'
      }
      const num = typeof value === 'number' ? value : parseFloat(String(value).trim().replace(',', '.'))
      if (Number.isNaN(num)) {
        return '—'
      }
      return num.toLocaleString('es-ES', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })
    },
  },
}
</script>

<style scoped>
.weekly-linked-boq-roots-modal {
  padding: 1rem 1.25rem 1.25rem;
  background-color: #fff;
  border-radius: 0.375rem;
}

.weekly-linked-boq-roots-modal__header {
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
}

.weekly-linked-boq-roots-modal__title-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.weekly-linked-boq-roots-modal__title-icon {
  color: #3b66bc;
}

.weekly-linked-boq-roots-modal__title {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #1f2937;
}

.weekly-linked-boq-roots-modal__subtitle {
  margin: 0.375rem 0 0;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #6b7280;
}

.weekly-linked-boq-roots-modal__primary-warning {
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
  margin: 0.5rem 0 0;
  padding: 0.5rem 0.625rem;
  font-size: 0.6875rem;
  line-height: 1.4;
  color: #92400e;
  background-color: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 0.25rem;
}

.weekly-linked-boq-roots-modal__primary-warning-icon {
  flex-shrink: 0;
  margin-top: 0.125rem;
  color: #d97706;
}

.weekly-linked-boq-roots-modal__table-wrap {
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
}

.weekly-linked-boq-roots-modal__table {
  background: #fff;
}

.weekly-linked-boq-roots-modal__th {
  padding: 0.5rem 0.625rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-align: center;
  color: #6b7280;
  white-space: nowrap;
}

.weekly-linked-boq-roots-modal__th--left {
  text-align: left;
}

.weekly-linked-boq-roots-modal__th--right {
  text-align: right;
}

.weekly-linked-boq-roots-modal__th--star {
  width: 2.5rem;
}

.weekly-linked-boq-roots-modal__row:not(:last-child) .weekly-linked-boq-roots-modal__td {
  border-bottom: 1px solid #f3f4f6;
}

.weekly-linked-boq-roots-modal__td {
  padding: 0.625rem;
  vertical-align: middle;
  color: #374151;
}

.weekly-linked-boq-roots-modal__code {
  font-weight: 600;
  color: #1f2937;
}

.weekly-linked-boq-roots-modal__name {
  max-width: 16rem;
  line-height: 1.35;
}

.weekly-linked-boq-roots-modal__star-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  border: 0;
  border-radius: 0.25rem;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.weekly-linked-boq-roots-modal__star-btn--on {
  color: #f59e0b;
  cursor: default;
}

.weekly-linked-boq-roots-modal__star-btn--off {
  color: #d1d5db;
}

.weekly-linked-boq-roots-modal__star-btn--off:not(:disabled):hover {
  color: #f59e0b;
  background-color: #fffbeb;
}

.weekly-linked-boq-roots-modal__star-btn--loading {
  color: #9ca3af;
  cursor: wait;
}

.weekly-linked-boq-roots-modal__star--on {
  color: #f59e0b;
}

.weekly-linked-boq-roots-modal__star--off {
  color: #d1d5db;
}

.weekly-linked-boq-roots-modal__qty {
  font-weight: 700;
  color: #1f2937;
}

.weekly-linked-boq-roots-modal__qty--total {
  font-size: 0.875rem;
}

.weekly-linked-boq-roots-modal__qty-unit {
  margin-left: 0.25rem;
  font-size: 0.6875rem;
  color: #6b7280;
}

.weekly-linked-boq-roots-modal__total-row {
  background: #f9fafb;
}

.weekly-linked-boq-roots-modal__total-label {
  padding: 0.625rem;
  text-align: right;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6b7280;
}

.weekly-linked-boq-roots-modal__hint {
  margin: 0.75rem 0 0;
  font-size: 0.6875rem;
  font-style: italic;
  color: #9ca3af;
}
</style>
